import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    FileText,
    User,
    Briefcase,
    Upload,
    X,
    Check,
    AlertTriangle,
    Loader2,
    Trash2,
    Eye,
    Building2,
    Mail,
    Image as ImageIcon,
    XCircle,
    CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../api/config';
import toast from 'react-hot-toast';
import SuccessSubmission from '../components/SuccessSubmission';

// ─────────────────────────────────────────────
// Shared: Offer Already Responded Banner
// ─────────────────────────────────────────────
const OfferStatusBanner = ({ type, brand, contact }) => {
    // type: 'already_accepted' | 'accepted_cannot_reject' | 'rejected_cannot_accept' | 'already_rejected'
    const configs = {
        already_accepted: {
            bg: 'from-emerald-50 via-white to-green-50',
            border: 'border-emerald-100',
            title: 'Offer Already Accepted',
            message: 'You have already accepted this offer. Welcome aboard! Our HR team will be in touch shortly.',
            contactColor: 'text-emerald-600',
        },
        accepted_cannot_reject: {
            bg: 'from-amber-50 via-white to-yellow-50',
            border: 'border-amber-100',
            title: 'Offer Already Accepted',
            message: 'This offer has already been accepted and cannot be declined. If you have concerns, please contact our HR team.',
            contactColor: 'text-amber-600',
        },
        rejected_cannot_accept: {
            bg: 'from-slate-50 via-white to-gray-50',
            border: 'border-slate-200',
            title: 'Offer Previously Declined',
            message: 'This offer has already been declined and cannot be accepted. If you believe this was a mistake, please reach out to our HR team.',
            contactColor: 'text-[#0F2942]',
        },
        already_rejected: {
            bg: 'from-red-50 via-white to-rose-50',
            border: 'border-red-100',
            title: 'Offer Already Declined',
            message: 'You have already declined this offer. No further action is needed. We wish you the very best in your future endeavors.',
            contactColor: 'text-red-500',
        },
    };
    const cfg = configs[type] || configs.already_rejected;
    return (
        <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${cfg.bg} p-6`}>
            <div className={`max-w-md w-full bg-white rounded-3xl shadow-xl border ${cfg.border} p-8 text-center`}>
                {brand?.logoUrl && <img src={brand.logoUrl} alt={brand.name} className="h-12 mx-auto mb-6 object-contain" />}
                <h1 className="text-xl font-bold text-gray-900 mb-3">{cfg.title}</h1>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{cfg.message}</p>
                {process.env.REACT_APP_HR_EMAIL && (
                    <p className="text-sm text-gray-400">
                        If you have any questions or need further assistance, please contact us at{' '}
                        <a href={`mailto:${process.env.REACT_APP_HR_EMAIL}`} className={`${cfg.contactColor} font-semibold underline`}>
                            {process.env.REACT_APP_HR_EMAIL}
                        </a>
                    </p>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// Reject Flow
// ─────────────────────────────────────────────
const OfferRejectPage = ({ offerId, offer, brand, contact }) => {
    const [reason, setReason] = useState('');
    const [reasonError, setReasonError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [rejected, setRejected] = useState(false);

    // Status guards — show appropriate banner if offer already responded to
    if (offer?.status === 'rejected') {
        return <OfferStatusBanner type="already_rejected" brand={brand} contact={contact} />;
    }
    if (offer?.status === 'accepted') {
        return <OfferStatusBanner type="accepted_cannot_reject" brand={brand} contact={contact} />;
    }

    if (rejected) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-rose-50 p-6">
                <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-red-100 p-8 text-center">
                    {brand?.logoUrl && <img src={brand.logoUrl} alt={brand.name} className="h-12 mx-auto mb-6 object-contain" />}
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Thank you for your response.</h1>
                    <p className="text-gray-500 text-sm mb-4">
                        You have declined the offer. We appreciate your time and honesty.<br />
                        We wish you the very best in your future endeavors.
                    </p>
                    {reason && (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-left mb-6">
                            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">Your Reason</p>
                            <p className="text-sm text-gray-700 leading-relaxed first-letter:capitalize">{reason}</p>
                        </div>
                    )}
                    {process.env.REACT_APP_HR_EMAIL && (
                        <p className="text-sm text-gray-400">
                            Questions? <a href={`mailto:${process.env.REACT_APP_HR_EMAIL}`} className="text-[#0F2942] font-semibold underline">{process.env.REACT_APP_HR_EMAIL}</a>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const handleReject = async (e) => {
        e.preventDefault();
        if (!reason.trim()) {
            setReasonError('Please provide a reason before declining.');
            return;
        }
        setReasonError('');
        setSubmitting(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/offer/${offerId}/reject`, {
                rejectionReason: reason.trim()
            });
            if (res.data.success) {
                setRejected(true);
            } else {
                toast.error(res.data.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const candidateName = offer?.candidate
        ? `${offer.candidate.firstName || ''} ${offer.candidate.lastName || ''}`.trim()
        : '';
    const candidateEmail = offer?.candidate?.email || '';
    const position = offer?.job?.title || offer?.jobTitle || 'N/A';
    const department = offer?.job?.department || offer?.department || 'N/A';

    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-rose-50 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl">
                {/* Brand */}
                <div className="text-center mb-8">
                    {brand?.logoUrl
                        ? <img src={brand.logoUrl} alt={brand.name} className="h-12 mx-auto mb-2 object-contain" />
                        : <h2 className="text-xl font-bold text-gray-900">{brand?.name}</h2>
                    }
                </div>

                <div className="bg-white rounded-3xl shadow-xl border border-red-100 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-red-500 to-rose-500 px-8 py-6 text-white text-center">
                        <h1 className="text-2xl font-bold mb-1">Decline Job Offer</h1>
                        <p className="text-red-100 text-sm">Please review the details and provide your reason</p>
                    </div>

                    {/* Offer Details */}
                    <div className="grid grid-cols-2 gap-4 p-6 bg-gray-50 border-b border-gray-100">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-red-100 rounded-lg shrink-0">
                                <Briefcase className="w-4 h-4 text-red-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Position</p>
                                <p className="text-sm font-semibold text-gray-900 break-words">{position}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-rose-100 rounded-lg shrink-0">
                                <Building2 className="w-4 h-4 text-rose-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Department</p>
                                <p className="text-sm font-semibold text-gray-900 break-words">{department}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                                <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Candidate</p>
                                <p className="text-sm font-semibold text-gray-900 break-words">{candidateName}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-orange-100 rounded-lg shrink-0">
                                <Mail className="w-4 h-4 text-orange-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Email</p>
                                <p className="text-sm font-semibold text-gray-900 break-all">{candidateEmail}</p>
                            </div>
                        </div>
                    </div>

                    {/* Rejection Reason Form */}
                    <form onSubmit={handleReject} className="p-6">
                        <div className="mb-6">
                            <label className="block text-sm font-bold text-gray-700 mb-2">
                                Reason for Declining <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={reason}
                                onChange={(e) => {
                                    setReason(e.target.value);
                                    if (e.target.value.trim()) setReasonError('');
                                }}
                                placeholder="Please share your reason for declining this offer..."
                                maxLength={1000}
                                rows={4}
                                className={`w-full px-4 py-3 border-2 rounded-xl text-sm font-medium text-gray-800 resize-y transition-all outline-none placeholder-gray-400
                                    ${reasonError
                                        ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                                        : 'border-gray-200 bg-white focus:border-red-400 focus:ring-4 focus:ring-red-100'
                                    }`}
                            />
                            {reasonError && (
                                <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> {reasonError}
                                </p>
                            )}
                            <p className="mt-1 text-xs text-gray-400 text-right">{reason.length}/1000</p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => window.close()}
                                className="flex-1 py-3 px-5 bg-gray-100 text-gray-600 font-semibold rounded-xl border-2 border-gray-200 hover:bg-gray-200 hover:border-gray-300 transition-all text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 py-3 px-5 bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-red-600 hover:to-rose-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                                ) : (
                                    <><XCircle className="w-4 h-4" /> Decline Offer</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="mt-6 text-center text-gray-400 text-xs">
                    <p className="font-semibold text-gray-500">{brand?.name}</p>
                    <p>Powering fair and transparent recruitment workflows.</p>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// Accept Flow (keep existing logic)
// ─────────────────────────────────────────────
const OfferAcceptancePage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action') || 'accept_with_documents';

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [offer, setOffer] = useState(null);
    const [brand, setBrand] = useState({});
    const [contact, setContact] = useState({});
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const [documents, setDocuments] = useState({
        profilePic: null,
        panCard: null,
        aadharCard: null,
        passport: null,
        degreeCertificate: [],
        experienceCertificate: null,
        addressProof: null,
        bankDetails: null,
        otherDocument: []
    });

    const [uploadProgress, setUploadProgress] = useState({});

    const documentFields = [
        { id: 'profilePic', label: 'Profile Picture', required: true, icon: <ImageIcon className="w-5 h-5" />, accept: 'image/*' },
        { id: 'panCard', label: 'PAN Card', required: true, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png' },
        { id: 'aadharCard', label: 'Aadhar Card', required: true, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png' },
        { id: 'degreeCertificate', label: 'Degree Certificate', required: false, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png', multiple: true },
        { id: 'addressProof', label: 'Address Proof', required: false, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png' },
        { id: 'passport', label: 'Passport', required: false, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png' },
        { id: 'experienceCertificate', label: 'Experience Certificate', required: false, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png' },
        { id: 'bankDetails', label: 'Bank Details', required: false, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png' },
        { id: 'otherDocument', label: 'Other Documents', required: false, icon: <FileText className="w-5 h-5" />, accept: '.pdf,.jpg,.jpeg,.png', multiple: true },
    ];

    useEffect(() => {
        fetchOfferDetails();
    }, [id]);

    const fetchOfferDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/offer/${id}/details`);
            if (response.data.success) {
                const { offer, brand, contact } = response.data.data;
                if (offer.candidate && offer.candidate.documents && offer.candidate.documents.length > 0) {
                    setAlreadySubmitted(true);
                }
                setOffer(offer);
                setBrand(brand);
                setContact(contact);
            } else {
                setError(response.data.message || 'Failed to load offer details');
            }
        } catch (err) {

            setError(err.response?.data?.message || 'Offer not found or link expired.');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (fieldId, file, isMultiple = false) => {
        if (!file) return;
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        if (fieldId === 'profilePic' && !file.type.startsWith('image/')) {
            toast.error('Profile picture must be an image (JPG, PNG).');
            return;
        }
        if (!validTypes.includes(file.type)) {
            toast.error('Only PDF, JPG, or PNG files are allowed.');
            return;
        }
        if (file.size > 25 * 1024 * 1024) {
            toast.error('File size cannot exceed 25MB.');
            return;
        }
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await axios.post(`${API_BASE_URL}/offer/${id}/upload-single-document`, formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(prev => ({ ...prev, [fieldId]: percentCompleted }));
                }
            });
            if (response.data.success) {
                const docData = {
                    url: response.data.data.url,
                    publicId: response.data.data.public_id,
                    fileSize: response.data.data.fileSize,
                    originalName: response.data.data.originalName,
                    documentType: fieldId === 'otherDocument' ? 'other' : fieldId.replace(/([A-Z])/g, '_$1').toLowerCase()
                };
                if (isMultiple) {
                    setDocuments(prev => ({ ...prev, [fieldId]: [...prev[fieldId], docData] }));
                } else {
                    setDocuments(prev => ({ ...prev, [fieldId]: docData }));
                }
                toast.success(`${file.name} uploaded successfully`);
            }
        } catch (err) {

            toast.error(err.response?.data?.responseMessage || 'Upload failed');
        } finally {
            setUploadProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[fieldId];
                return newProgress;
            });
        }
    };

    const getDocumentResourceType = (fieldId, doc) => {
        if (fieldId === 'profilePic') return 'image';

        const name = String(doc?.originalName || '').toLowerCase();
        if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp')) {
            return 'image';
        }
        return 'raw';
    };

    const removeDocument = async (fieldId, index = -1) => {
        const targetDoc = index !== -1
            ? documents?.[fieldId]?.[index]
            : documents?.[fieldId];

        if (!targetDoc) return;

        const publicId = String(targetDoc.publicId || targetDoc.public_id || '').trim();
        const resourceType = getDocumentResourceType(fieldId, targetDoc);

        try {
            setUploadProgress(prev => ({ ...prev, [fieldId]: 1 }));

            if (publicId) {
                const response = await axios.delete(
                    `${API_BASE_URL}/offer/${id}/delete-document`,
                    {
                        params: {
                            public_id: publicId,
                            resource_type: resourceType,
                        },
                    },
                );

                if (!response?.data?.success) {
                    throw new Error(response?.data?.responseMessage || 'Failed to delete document from server');
                }
            }

            if (index !== -1) {
                setDocuments(prev => ({ ...prev, [fieldId]: prev[fieldId].filter((_, i) => i !== index) }));
            } else {
                setDocuments(prev => ({ ...prev, [fieldId]: null }));
            }

            toast.success('Document deleted successfully');
        } catch (err) {

            toast.error(err?.response?.data?.responseMessage || err?.message || 'Failed to delete document');
        } finally {
            setUploadProgress(prev => {
                const next = { ...prev };
                delete next[fieldId];
                return next;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const missing = documentFields.filter(f => f.required && !documents[f.id]).map(f => f.label);
        if (missing.length > 0) {
            toast.error(`Please upload required documents: ${missing.join(', ')}`);
            return;
        }
        try {
            setSubmitting(true);
            const submissionData = { documents: {} };
            Object.keys(documents).forEach(key => {
                if (documents[key]) submissionData.documents[key] = documents[key];
            });
            const response = await axios.post(`${API_BASE_URL}/offer/${id}/upload-documents`, submissionData);
            if (response.data.success || response.status === 200) {
                if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
                    document.open();
                    document.write(response.data);
                    document.close();
                    return;
                }
                setSuccess(true);
                toast.success('Documents submitted successfully!');
            }
        } catch (err) {

            toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Loading ──
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-agrosaf-green animate-spin mb-4" />
                    <p className="text-gray-600 font-medium">Loading offer details...</p>
                </div>
            </div>
        );
    }

    // ── Error ──
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-red-100">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertTriangle className="w-10 h-10 text-red-500" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h1>
                    <p className="text-gray-600 mb-8">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full bg-gray-900 text-white font-semibold py-3 rounded-xl hover:bg-gray-800 transition-colors"
                    >
                        Try Again
                    </button>
                    {process.env.REACT_APP_HR_EMAIL && (
                        <p className="mt-6 text-sm text-gray-500">
                            Need help? Contact <a href={`mailto:${process.env.REACT_APP_HR_EMAIL}`} className="text-agrosaf-green font-medium underline">{process.env.REACT_APP_HR_EMAIL}</a>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // ── STATUS GUARDS (cross-action): check offer.status vs requested action ──
    if (offer?.status === 'accepted') {
        if (action === 'reject') {
            // Candidate accepted, now trying to reject
            return <OfferStatusBanner type="accepted_cannot_reject" brand={brand} contact={contact} />;
        }
        // Candidate accepted, trying to accept again
        return <OfferStatusBanner type="already_accepted" brand={brand} contact={contact} />;
    }

    if (offer?.status === 'rejected') {
        if (action === 'reject') {
            // Already rejected, trying to reject again
            return <OfferStatusBanner type="already_rejected" brand={brand} contact={contact} />;
        }
        // Already rejected, now trying to accept
        return <OfferStatusBanner type="rejected_cannot_accept" brand={brand} contact={contact} />;
    }

    // ── REJECT FLOW (offer is still pending/sent) ──
    if (action === 'reject') {
        return <OfferRejectPage offerId={id} offer={offer} brand={brand} contact={contact} />;
    }

    // ── ACCEPT FLOW: already submitted ──
    if (success || alreadySubmitted) {
        return (
            <SuccessSubmission
                offer={offer}
                brand={brand}
                contact={contact}
                alreadySubmitted={alreadySubmitted}
                onClose={() => window.close()}
            />
        );
    }

    // ── ACCEPT FLOW: document upload form ──
    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 md:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Branding */}
                <div className="mb-10 text-center">
                    {brand.logoUrl ? (
                        <img src={brand.logoUrl} alt={brand.name} className="h-16 mx-auto mb-4 object-contain" />
                    ) : (
                        <h1 className="text-3xl font-bold text-gray-900">{brand.name}</h1>
                    )}
                </div>

                <div className="bg-white rounded-3xl shadow-soft-xl border border-gray-100 overflow-hidden">
                    {/* Header Banner */}
                    <div className="bg-gradient-to-r from-agrosaf-green to-emerald-500 p-6 text-white">
                        <h1 className="text-2xl md:text-3xl font-bold mb-3">Document Submission</h1>
                        <p className="text-green-50 opacity-90 text-lg max-w-2xl">
                            Welcome abord! Please provide your official documents to complete the offer acceptance and onboarding process.
                        </p>
                    </div>

                    {/* Offer Info Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 bg-gray-50 border-b border-gray-100">
                        <div className="flex items-start space-x-3">
                            <div className="p-2 bg-green-100 rounded-lg text-agrosaf-green">
                                <Briefcase className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Position</p>
                                <p className="text-gray-900 font-semibold break-words">{offer.job?.title || offer.jobTitle}</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg text-[#0F2942]">
                                <Building2 className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Department</p>
                                <p className="text-gray-900 font-semibold break-words">{offer.job?.department || offer.department}</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                                <User className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Candidate</p>
                                <p className="text-gray-900 font-semibold break-words capitalize">{offer.candidate.firstName} {offer.candidate.lastName}</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                                <Mail className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Email</p>
                                <p className="text-gray-900 font-semibold break-words">{offer.candidate.email}</p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        {/* Mandatory Documents Section */}
                        <div className="mb-8">
                            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                                <span className="w-6 h-6 bg-agrosaf-green text-white rounded-lg flex items-center justify-center mr-3 text-sm">1</span>
                                Mandatory Documents
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {documentFields.filter(f => f.required).map(field => (
                                    <div key={field.id} className="space-y-3">
                                        <label className="text-sm font-bold text-gray-700 flex justify-between">
                                            <span>{field.label} <span className="text-red-500">*</span></span>
                                        </label>

                                        {!documents[field.id] ? (
                                            <div
                                                className={`relative border-2 border-dashed rounded-2xl p-4 transition-all cursor-pointer group flex flex-col items-center justify-center min-h-[110px]
                                                    ${uploadProgress[field.id] ? 'bg-gray-50 border-gray-200 cursor-wait' : 'bg-white border-gray-200 hover:border-agrosaf-green hover:bg-green-50'}`}
                                                onClick={() => !uploadProgress[field.id] && document.getElementById(`input-${field.id}`).click()}
                                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-agrosaf-green', 'bg-green-50'); }}
                                                onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-agrosaf-green', 'bg-green-50'); }}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.currentTarget.classList.remove('border-agrosaf-green', 'bg-green-50');
                                                    if (e.dataTransfer.files.length > 0) handleFileUpload(field.id, e.dataTransfer.files[0]);
                                                }}
                                            >
                                                <input
                                                    type="file"
                                                    id={`input-${field.id}`}
                                                    className="hidden"
                                                    accept={field.accept}
                                                    onChange={(e) => e.target.files.length > 0 && handleFileUpload(field.id, e.target.files[0])}
                                                />

                                                {uploadProgress[field.id] ? (
                                                    <div className="w-full max-w-[160px] text-center">
                                                        <Loader2 className="w-8 h-8 text-agrosaf-green animate-spin mx-auto mb-2" />
                                                        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                                                            <div className="bg-agrosaf-green h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[field.id]}%` }}></div>
                                                        </div>
                                                        <span className="text-xs font-bold text-gray-500 uppercase">{uploadProgress[field.id]}% Uploading</span>
                                                    </div>
                                                ) : (
                                                    <div className="text-center group-hover:transform group-hover:scale-110 transition-transform">
                                                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400 group-hover:bg-agrosaf-green group-hover:text-white transition-colors">
                                                            <Upload className="w-5 h-5" />
                                                        </div>
                                                        <p className="text-xs font-bold text-gray-900">
                                                            {field.id === 'profilePic' ? 'Any Image Format' : 'PDF, JPG, PNG'}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">Max 25MB accepted</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="relative bg-white border border-gray-100 rounded-2xl p-3 shadow-sm flex items-center group animate-in fade-in slide-in-from-bottom-2 duration-300">
                                                <div className={`p-2 rounded-xl mr-4 ${field.id === 'profilePic' ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-agrosaf-green'}`}>
                                                    {field.icon}
                                                </div>
                                                <div className="flex-1 min-w-0 pr-2">
                                                    <p className="text-sm font-bold text-gray-900 truncate">{documents[field.id].originalName}</p>
                                                    <p className="text-xs text-gray-500">{(documents[field.id].fileSize / 1024 / 1024).toFixed(2)} MB • Uploaded</p>
                                                </div>
                                                <div className="ml-4 flex items-center space-x-2 flex-shrink-0">
                                                    <a
                                                        href={documents[field.id].url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-2 text-gray-400 hover:text-agrosaf-green hover:bg-green-50 rounded-lg transition-colors"
                                                        title="View File"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </a>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDocument(field.id)}
                                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remove File"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Additional Documents Section */}
                        <div className="mb-8">
                            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                                <span className="w-6 h-6 bg-blue-500 text-white rounded-lg flex items-center justify-center mr-3 text-sm">2</span>
                                Additional Documents (Optional)
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {documentFields.filter(f => !f.required && f.id !== 'otherDocument').map(field => (
                                    <div key={field.id} className="space-y-3">
                                        <label className="text-sm font-bold text-gray-700 flex justify-between">
                                            <span>{field.label}</span>
                                        </label>

                                        {field.multiple ? (
                                            <>
                                                <div
                                                    className={`relative border-2 border-dashed rounded-2xl p-4 transition-all cursor-pointer group flex flex-col items-center justify-center min-h-[110px]
                                                        ${uploadProgress[field.id] ? 'bg-gray-50 border-gray-200 cursor-wait' : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50'}`}
                                                    onClick={() => !uploadProgress[field.id] && document.getElementById(`input-${field.id}`).click()}
                                                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                                                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                                                        if (e.dataTransfer.files.length > 0) handleFileUpload(field.id, e.dataTransfer.files[0], true);
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        id={`input-${field.id}`}
                                                        className="hidden"
                                                        accept={field.accept}
                                                        onChange={(e) => e.target.files.length > 0 && handleFileUpload(field.id, e.target.files[0], true)}
                                                    />
                                                    {uploadProgress[field.id] ? (
                                                        <div className="w-full max-w-[160px] text-center">
                                                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                                                            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                                                                <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[field.id]}%` }}></div>
                                                            </div>
                                                            <span className="text-xs font-bold text-gray-500 uppercase">{uploadProgress[field.id]}% Uploading</span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center group-hover:transform group-hover:scale-110 transition-transform">
                                                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                                                                <Upload className="w-5 h-5" />
                                                            </div>
                                                            <p className="text-xs font-bold text-gray-900">PDF, JPG, PNG</p>
                                                            <p className="text-xs text-gray-500 mt-1">Max 25MB accepted</p>
                                                        </div>
                                                    )}
                                                </div>
                                                {documents[field.id] && documents[field.id].length > 0 && (
                                                    <div className="pt-2 grid gap-2">
                                                        {documents[field.id].map((doc, idx) => (
                                                            <div key={idx} className="relative bg-white border border-gray-100 rounded-2xl p-3 shadow-sm flex items-center group hover:shadow-md transition-shadow">
                                                                <div className="p-2 bg-blue-100 rounded-xl mr-4 text-[#0F2942]">{field.icon}</div>
                                                                <div className="flex-1 min-w-0 pr-2">
                                                                    <p className="text-sm font-bold text-gray-900 truncate">{doc.originalName}</p>
                                                                    <p className="text-xs text-gray-500">{(doc.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                                                                </div>
                                                                <div className="ml-4 flex items-center space-x-1 flex-shrink-0">
                                                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-[#0F2942] hover:bg-blue-50 rounded-lg transition-colors">
                                                                        <Eye className="w-5 h-5" />
                                                                    </a>
                                                                    <button type="button" onClick={() => removeDocument(field.id, idx)} className="p-1 text-gray-400 hover:text-red-500 rounded-md transition-colors">
                                                                        <X className="w-5 h-5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            !documents[field.id] ? (
                                                <div
                                                    className={`relative border-2 border-dashed rounded-2xl p-4 transition-all cursor-pointer group flex flex-col items-center justify-center min-h-[110px]
                                                        ${uploadProgress[field.id] ? 'bg-gray-50 border-gray-200 cursor-wait' : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50'}`}
                                                    onClick={() => !uploadProgress[field.id] && document.getElementById(`input-${field.id}`).click()}
                                                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
                                                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                                                        if (e.dataTransfer.files.length > 0) handleFileUpload(field.id, e.dataTransfer.files[0]);
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        id={`input-${field.id}`}
                                                        className="hidden"
                                                        accept={field.accept}
                                                        onChange={(e) => e.target.files.length > 0 && handleFileUpload(field.id, e.target.files[0])}
                                                    />
                                                    {uploadProgress[field.id] ? (
                                                        <div className="w-full max-w-[160px] text-center">
                                                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                                                            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                                                                <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress[field.id]}%` }}></div>
                                                            </div>
                                                            <span className="text-xs font-bold text-gray-500 uppercase">{uploadProgress[field.id]}% Uploading</span>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center group-hover:transform group-hover:scale-110 transition-transform">
                                                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                                                                <Upload className="w-5 h-5" />
                                                            </div>
                                                            <p className="text-xs font-bold text-gray-900">PDF, JPG, PNG</p>
                                                            <p className="text-xs text-gray-500 mt-1">Max 25MB accepted</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="relative bg-white border border-gray-100 rounded-2xl p-3 shadow-sm flex items-center group animate-in fade-in slide-in-from-bottom-2 duration-300">
                                                    <div className="p-2 bg-blue-100 rounded-xl mr-4 text-[#0F2942]">{field.icon}</div>
                                                    <div className="flex-1 min-w-0 pr-2">
                                                        <p className="text-sm font-bold text-gray-900 truncate">{documents[field.id].originalName}</p>
                                                        <p className="text-xs text-gray-500">{(documents[field.id].fileSize / 1024 / 1024).toFixed(2)} MB • Uploaded</p>
                                                    </div>
                                                    <div className="ml-4 flex items-center space-x-2 flex-shrink-0">
                                                        <a href={documents[field.id].url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-[#0F2942] hover:bg-blue-50 rounded-lg transition-colors" title="View File">
                                                            <Eye className="w-5 h-5" />
                                                        </a>
                                                        <button type="button" onClick={() => removeDocument(field.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove File">
                                                            <Trash2 className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Other Documents Section */}
                        <div className="mb-8">
                            <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                                <span className="w-6 h-6 bg-purple-500 text-white rounded-lg flex items-center justify-center mr-3 text-sm">3</span>
                                Other Relevant Documents
                            </h2>
                            <div className="space-y-4">
                                <div
                                    className={`relative border-2 border-dashed rounded-2xl p-6 transition-all cursor-pointer group flex flex-col items-center justify-center min-h-[120px]
                                        ${uploadProgress['otherDocument'] ? 'bg-gray-50 border-gray-200 cursor-wait' : 'bg-white border-gray-200 hover:border-purple-500 hover:bg-purple-50'}`}
                                    onClick={() => !uploadProgress['otherDocument'] && document.getElementById(`input-otherDocument`).click()}
                                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-purple-500', 'bg-purple-50'); }}
                                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50'); }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                                        if (e.dataTransfer.files.length > 0) handleFileUpload('otherDocument', e.dataTransfer.files[0], true);
                                    }}
                                >
                                    <input
                                        type="file"
                                        id={`input-otherDocument`}
                                        className="hidden"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => e.target.files.length > 0 && handleFileUpload('otherDocument', e.target.files[0], true)}
                                    />
                                    {uploadProgress['otherDocument'] ? (
                                        <div className="w-full max-w-[200px] text-center">
                                            <Loader2 className="w-10 h-10 text-purple-500 animate-spin mx-auto mb-3" />
                                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                                                <div className="bg-purple-500 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress['otherDocument']}%` }}></div>
                                            </div>
                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">{uploadProgress['otherDocument']}% Please wait</span>
                                        </div>
                                    ) : (
                                        <div className="text-center group-hover:transform group-hover:scale-110 transition-transform">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400 group-hover:bg-purple-500 group-hover:text-white transition-colors shadow-sm">
                                                <Upload className="w-6 h-6" />
                                            </div>
                                            <p className="text-base font-bold text-gray-900 mb-1">PDF, JPG, PNG</p>
                                            <p className="text-sm text-gray-500">Max 25MB accepted</p>
                                        </div>
                                    )}
                                </div>
                                {documents.otherDocument.length > 0 && (
                                    <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {documents.otherDocument.map((doc, idx) => (
                                            <div key={idx} className="relative bg-white border border-gray-100 rounded-2xl p-3 shadow-sm flex items-center group hover:shadow-md transition-shadow">
                                                <div className="p-2 bg-purple-100 rounded-xl mr-4 text-purple-600">
                                                    <FileText className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0 pr-2">
                                                    <p className="text-sm font-bold text-gray-900 truncate">{doc.originalName}</p>
                                                    <p className="text-xs text-gray-500">{(doc.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                                                </div>
                                                <div className="ml-4 flex items-center space-x-1 flex-shrink-0">
                                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors">
                                                        <Eye className="w-5 h-5" />
                                                    </a>
                                                    <button type="button" onClick={() => removeDocument('otherDocument', idx)} className="p-1 text-gray-400 hover:text-red-500 rounded-md transition-colors">
                                                        <X className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-10 pt-6 border-t border-gray-100 text-center">
                            <button
                                type="submit"
                                disabled={submitting || !documentFields.every(field => !field.required || documents[field.id])}
                                className={`group relative w-full md:w-auto md:min-w-[280px] px-8 py-3 font-bold text-lg rounded-2xl transition-all transform active:translate-y-0 disabled:cursor-not-allowed
                                    ${(submitting || !documentFields.every(field => !field.required || documents[field.id]))
                                        ? 'bg-gray-300 text-gray-500 shadow-none'
                                        : 'bg-agrosaf-green bg-gradient-to-r from-agrosaf-green to-emerald-600 text-white shadow-xl hover:shadow-green-200 hover:-translate-y-1'}`}
                            >
                                {submitting ? (
                                    <span className="flex items-center justify-center">
                                        <Loader2 className="w-6 h-6 animate-spin mr-3" />
                                        Processing Submission...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        Submit Documents
                                        {!documentFields.every(field => !field.required || documents[field.id]) ? (
                                            <AlertTriangle className="w-5 h-5 ml-3" />
                                        ) : (
                                            <Check className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
                                        )}
                                    </span>
                                )}
                            </button>
                            <p className="mt-6 text-sm text-gray-400 font-medium">
                                By submitting, you confirm that all provided documents are authentic and valid.
                            </p>
                        </div>
                    </form>
                </div>

                {/* Footer Section */}
                <div className="mt-12 text-center text-gray-400 ">
                    <p className="font-bold text-lg text-gray-500 mb-2">{brand.name}</p>
                    <p className="text-sm">Powering fair and transparent recruitment workflows.</p>
                </div>
            </div>
        </div>
    );
};

export default OfferAcceptancePage;
